import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AccountActions } from '@/account/store/slice'
import { AuthContext } from '@/context/auth-context'
import { SubscriptionStatus } from '@/firebase/auth/auth-jwt-schema'
import { useAppDispatch } from '@/store/store-hooks'
import { ApiError } from '@/util/errors/api-error'
import { getSubscriptionActivation, postSubscriptionActivation } from '../fetch/fetch-subscription-activation'
import {
  SubscriptionActivationConfirmation,
  SubscriptionActivationConfirmationSchema,
  SubscriptionActivationResponse,
  SubscriptionActivationStatus,
} from '../schemas'

export function useSubscriptionActivation(onActivated: () => Promise<void>) {
  const dispatch = useAppDispatch()
  const { user, userAccountData, refreshUserAccountData } = useContext(AuthContext)
  const accountKey = `${user?.uid ?? ''}:${userAccountData?.orgData?.id ?? ''}`
  // Legacy organization access can be "paid" during a trial; the API owns billing eligibility.
  const shouldLoad = Boolean(user?.uid && (userAccountData?.orgData || userAccountData?.subscriptionStatus === SubscriptionStatus.TRIAL))
  const [state, setState] = useState<{
    accountKey: string
    data: SubscriptionActivationResponse | null
    open: boolean
    busy: boolean
    error: string | null
  }>({ accountKey, data: null, open: false, busy: false, error: null })
  const currentKey = useRef(accountKey)
  currentKey.current = accountKey
  const generation = useRef(0)
  const inFlight = useRef(false)
  const confirmation = useRef<SubscriptionActivationConfirmation | null>(null)
  const onActivatedRef = useRef(onActivated)
  onActivatedRef.current = onActivated
  const refreshAccountRef = useRef(refreshUserAccountData)
  refreshAccountRef.current = refreshUserAccountData

  const request = useCallback(
    async (activate = false) => {
      if (inFlight.current || currentKey.current !== accountKey) return
      inFlight.current = true
      const requestGeneration = ++generation.current
      const stillCurrent = () => generation.current === requestGeneration && currentKey.current === accountKey
      setState((previous) => ({ ...previous, busy: true, error: null }))
      try {
        const next = confirmation.current ? await postSubscriptionActivation(confirmation.current, !activate) : await getSubscriptionActivation()
        if (!stillCurrent()) return
        setState((previous) => ({ ...previous, accountKey, data: next }))
        if (next.status === SubscriptionActivationStatus.PaymentRequired) {
          confirmation.current = SubscriptionActivationConfirmationSchema.parse(next.quote)
        } else if (next.status === SubscriptionActivationStatus.Ready || next.status === SubscriptionActivationStatus.PaymentMethodRequired) {
          confirmation.current = null
        }
        if (next.status === SubscriptionActivationStatus.Active) {
          dispatch(AccountActions.refreshPaymentDetails({ forceRefresh: true }))
          await Promise.all([refreshAccountRef.current(), onActivatedRef.current()])
        }
      } catch (error) {
        if (!stillCurrent()) return
        if (error instanceof ApiError && error.status === 409) {
          confirmation.current = null
          const next = await getSubscriptionActivation().catch(() => null)
          if (!stillCurrent()) return
          setState((previous) => ({ ...previous, data: next, error: 'Billing details changed. Review them before confirming again.' }))
        } else {
          setState((previous) => ({
            ...previous,
            error: error instanceof ApiError ? error.message : 'Activation could not be confirmed. Check its status before retrying.',
          }))
        }
      } finally {
        if (stillCurrent()) {
          inFlight.current = false
          setState((previous) => ({ ...previous, busy: false }))
        }
      }
    },
    [accountKey, dispatch]
  )

  useEffect(() => {
    generation.current += 1
    inFlight.current = false
    confirmation.current = null
    setState({ accountKey, data: null, open: false, busy: false, error: null })
    return () => {
      generation.current += 1
    }
  }, [accountKey, request])

  useEffect(() => {
    if (shouldLoad) void request()
  }, [shouldLoad, request])

  useEffect(() => {
    if (!state.open || state.accountKey !== accountKey) return
    const refreshOnFocus = () => void request()
    window.addEventListener('focus', refreshOnFocus)
    return () => window.removeEventListener('focus', refreshOnFocus)
  }, [accountKey, request, state.accountKey, state.open])

  const current = state.accountKey === accountKey
  const data = current ? state.data : null
  const canActivate =
    shouldLoad && data !== null && data.status !== SubscriptionActivationStatus.Unavailable && data.status !== SubscriptionActivationStatus.Active
  return {
    data,
    open: current && state.open,
    busy: !current || state.busy,
    error: current ? state.error : null,
    onActivateSubscription: canActivate
      ? () => {
          setState((previous) => ({ ...previous, open: true }))
          void request()
        }
      : undefined,
    onClose: () => setState((previous) => ({ ...previous, open: false })),
    onRefresh: () => void request(),
    onConfirm: () => {
      if (!current || data?.status !== SubscriptionActivationStatus.Ready || inFlight.current) return
      confirmation.current = SubscriptionActivationConfirmationSchema.parse(data.quote)
      void request(true)
    },
  }
}
